import { useState, type ReactNode } from 'react';
import { A, sx } from './parts';
import type { ReceiverCard, SenderCard, SenderItem, WatchSignRow, WatchStepRow } from './v9';

// The V8 and V9 blocks on the Worklist, drawn as Aithora ConsoleV9.dc.html draws them (template
// T1442-1490 the bar, the hand-off and the staffing line; T1738-1795 the folded rows and the
// medicine watch; T1797-1885 the med pass "Also today" row and the safety cards). Every style
// string is the design's own, so the dark map (theme-dark.css) applies to it unchanged.

const KICKER = 'font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#2F5C97;';
const BTN_SOLID = 'display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
const BTN_LINE = 'display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
const BTN_LINE36 = 'display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
const TAG = 'display:inline-flex;align-items:center;padding:1px 8px;border-radius:6px;background:#F6F3EC;color:#3C454F;font-size:14px;font-weight:700;white-space:nowrap;line-height:1.35;';
const ROW = 'display:flex;align-items:flex-start;gap:4px 12px;flex-wrap:wrap;padding:7px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;';
const FOLD = 'flex:0 0 auto;display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;margin-bottom:8px;padding:8px 12px;border-radius:10px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);min-width:0;flex-direction:column;align-items:stretch;';

interface Off {
  disabled?: boolean;
  title?: string;
}

/** "Pick up where you left off": small, under the emergency, only on the page it points to. */
export function ResumeLine({ line, onGo, onNo }: { line: string; onGo: () => void; onNo: () => void }) {
  return (
    <div data-screen-label="Pick up where you left off" data-testid="worklist-resume" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;margin-bottom:10px;padding:4px 10px;border-radius:8px;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);min-width:0;')}>
      <span style={sx('flex:1 1 260px;min-width:0;font-size:14px;font-weight:700;color:#22272E;line-height:1.4;')}>{line}</span>
      <A style={BTN_SOLID} ink onClick={onGo}>
        Go back
      </A>
      <A style={BTN_LINE} onClick={onNo}>
        Not now
      </A>
    </div>
  );
}

/** The one button that opens search, codes, views and keys (V9 "Summary first"). */
export function ToolsToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <div style={sx('display:flex;justify-content:flex-end;')}>
      <A
        style="display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.35);"
        onClick={onToggle}
        testId="worklist-tools"
      >
        {open ? 'Hide search & shortcuts' : 'Search & shortcuts'}
      </A>
    </div>
  );
}

/** Views other than All with nothing left on this tab (design wlViewEmpty). */
export function ViewEmpty({ text, onAll }: { text: string; onAll: () => void }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:12px 14px;border-radius:10px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
      <span style={sx('font-size:16px;color:#22272E;flex:1 1 240px;min-width:0;')}>{text}</span>
      <A style="display:inline-flex;align-items:center;min-height:32px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.4);" onClick={onAll}>
        Show all tasks
      </A>
    </div>
  );
}

/** What moved to me while a colleague is pulled away (handoff.js rcv): Accept all, or pass it on. */
export function ReceiverCardView({ card, who, ro, onAccept, onPass, off }: { card: ReceiverCard; who: string; ro: boolean; onAccept: () => void; onPass: () => void; off: Off }) {
  return (
    <div data-screen-label="Hand-off · Receiver" data-testid="cover-receiver" style={sx('display:flex;flex-direction:column;gap:6px;padding:8px 0 4px;border-top:1px dashed rgba(47,92,151,.5);min-width:0;')}>
      <span style={sx(KICKER)}>{who}</span>
      <span style={sx('font-size:17px;font-weight:800;color:#22272E;line-height:1.35;')}>{card.head}</span>
      {card.accOpen && !ro && (
        <>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;align-items:center;')}>
            <A style={BTN_SOLID} ink onClick={onAccept} disabled={off.disabled} title={off.title}>
              Accept all
            </A>
            <A style={BTN_LINE} onClick={onPass} disabled={off.disabled} title={off.title}>
              Can&rsquo;t, pass on
            </A>
          </div>
          <span data-testid="cover-pass-in" style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
            {card.passIn === null
              ? 'Accept within 5 min (2 min for a time-critical dose) or it passes to the next eligible nurse.'
              : card.passIn > 0
                ? `Passes to the next eligible nurse in ${card.passIn} min unless you accept.`
                : 'Passing to the next eligible nurse now.'}
          </span>
        </>
      )}
      {!card.accOpen && <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;line-height:1.4;')}>Accepted. They are on your list now.</span>}
      {card.rows.map((x) => (
        <div key={x.key} style={sx(ROW)}>
          <div style={sx('flex:1 1 240px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
            <span style={sx('display:flex;align-items:center;gap:8px;')}>
              <span aria-hidden="true" style={sx('flex:0 0 32px;width:32px;height:32px;border-radius:50%;background:#264C7E;color:#FFFFFF;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;')}>
                {x.ini}
              </span>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;')}>{`${x.who} · ${x.task}`}</span>
            </span>
            {x.hot ? (
              <span style={sx('font-size:16px;font-weight:800;color:#B3261E;')}>{`${x.due}${x.win ? ` · ${x.win}` : ''}`}</span>
            ) : (
              <span style={sx('font-size:16px;color:#3C454F;')}>{`${x.due}${x.win ? ` · ${x.win}` : ''}`}</span>
            )}
            <span style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
              <span style={sx(TAG)}>{x.from}</span>
              <span style={sx(TAG)}>{x.why}</span>
            </span>
            {x.ladder && <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{x.ladder}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The person pulled away: where their work went, then Take back when they are free (handoff.js hoAway / hoBack). */
export function SenderCardView({ card, onTakeBack, onTakeOne, off }: { card: SenderCard; onTakeBack: () => void; onTakeOne: (ref: SenderItem['ref']) => void; off: Off }) {
  const [freed, setBack] = useState(false);
  const back = freed || card.over;
  return (
    <div data-screen-label="Worklist · Pulled away hand-off" data-testid="cover-sender" role="status" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:2px solid #2F5C97;min-width:0;')}>
      {!back ? (
        <>
          <span style={sx(KICKER)}>Pulled away · your tasks moved</span>
          <span style={sx('font-size:18px;font-weight:800;color:#22272E;line-height:1.3;')}>{card.head}</span>
          {card.items.map((x) => (
            <div key={x.key} style={sx(ROW)}>
              <span style={sx('flex:1 1 240px;min-width:0;font-size:16px;color:#22272E;')}>
                <b style={sx('font-weight:700;')}>{x.who}</b> · {x.task}
              </span>
              <span style={sx('font-size:16px;font-weight:700;color:#2F5C97;')}>{x.to}</span>
              {x.done && <span style={sx('flex:1 1 100%;font-size:16px;font-weight:700;color:#1F6B52;line-height:1.4;')}>{x.doneLine}</span>}
            </div>
          ))}
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            <A style={BTN_SOLID} ink onClick={() => setBack(true)}>
              I&rsquo;m free now
            </A>
          </div>
        </>
      ) : (
        <>
          <span style={sx(KICKER)}>Back from the emergency</span>
          <span style={sx('font-size:18px;font-weight:800;color:#22272E;line-height:1.3;')}>{card.backHead}</span>
          {card.items.map((x) => (
            <div key={x.key} style={sx(ROW)}>
              <div style={sx('flex:1 1 240px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx('font-size:16px;color:#22272E;')}>
                  <b style={sx('font-weight:700;')}>{x.who}</b> · {x.task}
                </span>
                {x.done && <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;line-height:1.4;')}>{x.doneLine}</span>}
                {!x.done && !x.open && <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;line-height:1.4;')}>Back on your list</span>}
                {x.open && <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{x.to.replace(/^→ /, 'With ')}</span>}
              </div>
              {x.open && (
                <A style={BTN_LINE} onClick={() => onTakeOne(x.ref)} disabled={off.disabled} title={off.title} testId={`cover-take-one-${x.ref.sourceId}`}>
                  Take back
                </A>
              )}
            </div>
          ))}
          {card.openN > 0 && (
            <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
              <A style={BTN_LINE} onClick={onTakeBack} disabled={off.disabled} title={off.title}>
                Take back all not done
              </A>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** The Facility Admin's and the read-only roles' one line, with Details (care-ops.js stLine). */
export function StaffingSummary({ line, open, onToggle, children }: { line: string; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <>
      <div data-screen-label="Worklist · Staffing summary" data-testid="worklist-staffing" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;margin-bottom:8px;padding:8px 12px;border-radius:10px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);min-width:0;')}>
        <span style={sx('flex:1 1 300px;min-width:0;font-size:16px;font-weight:700;color:#22272E;line-height:1.4;')}>{line}</span>
        <A style={BTN_LINE36} onClick={onToggle}>
          {open ? 'Hide' : 'Details'}
        </A>
      </div>
      {open && children}
    </>
  );
}

/** Details, first card: tonight's staffing. Neither line has a back end yet. */
export function TonightStaffing({ briefLine, callOutLine }: { briefLine: string; callOutLine: string }) {
  return (
    <div data-screen-label="Tonight's staffing" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);min-width:0;')}>
      <span style={sx(KICKER)}>Tonight&rsquo;s staffing</span>
      <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.4;')}>{briefLine}</span>
      <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{callOutLine}</span>
    </div>
  );
}

/** Details, second card: covering when staff are pulled away (handoff.js hoAdmin). */
export function CoverOversight({ head, sub, live, children }: { head: string; sub: string; live: string[]; children?: ReactNode }) {
  return (
    <div data-screen-label="Worklist · Hand-off oversight" role="status" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:10px 14px;border-radius:12px;background:#FCFAF4;border:2px solid #2F5C97;min-width:0;')}>
      <span style={sx(KICKER)}>Covering when staff are pulled away</span>
      <span style={sx('font-size:17px;font-weight:800;color:#22272E;line-height:1.35;')}>{head}</span>
      <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{sub}</span>
      {live.map((l) => (
        <span key={l} style={sx('font-size:16px;font-weight:700;color:#B3261E;line-height:1.4;')}>{`Live · ${l}`}</span>
      ))}
      {children && <div style={sx('display:flex;flex-direction:column;gap:6px;margin-top:4px;padding:10px 12px;border-radius:10px;border:1px dashed #2F5C97;background:#FFFFFF;min-width:0;')}>{children}</div>}
    </div>
  );
}

/** The receiver card on its own (Care Staff): the same card, in the hand-off frame. */
export function ReceiverFrame({ children }: { children: ReactNode }) {
  return (
    <div data-screen-label="Worklist · Covering for a colleague" role="status" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:2px solid #2F5C97;min-width:0;')}>
      {children}
    </div>
  );
}

/** Care Staff, under the tasks: the folded shift brief (care-ops.js brief). No route records a brief yet. */
export function ShiftBriefRow() {
  const [open, setOpen] = useState(false);
  return (
    <div data-screen-label="Shift brief" style={sx(FOLD)}>
      <div style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:700;color:#22272E;line-height:1.35;')}>Shift brief · none recorded for this shift</span>
        <A style={BTN_LINE36} onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Open'}
        </A>
      </div>
      {open && (
        <div style={sx('display:flex;flex-direction:column;gap:6px;padding-top:4px;')}>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>No shift brief has been recorded in Aithora for this shift. Ask your charge nurse what to watch for tonight.</span>
        </div>
      )}
    </div>
  );
}

/** Care Staff, under the tasks: More (call-out, and what happens if you are pulled away). */
export function MoreRow({ coverOn }: { coverOn: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-screen-label="More for your shift" style={sx(FOLD)}>
      <div style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:700;color:#22272E;line-height:1.35;')}>More: can&rsquo;t make a shift, and what happens if you&rsquo;re pulled away</span>
        <A style={BTN_LINE36} onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Open'}
        </A>
      </div>
      {open && (
        <div style={sx('display:flex;flex-direction:column;gap:8px;padding-top:4px;min-width:0;')}>
          <div data-screen-label="Call-out (in More)" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Can&rsquo;t make a shift?</span>
            <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>Call-out fill is not connected yet. Tell your charge nurse or the scheduler.</span>
          </div>
          {coverOn && <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;margin-bottom:8px;')}>Pulled away? Tap &ldquo;I&rsquo;m going&rdquo; on the emergency and your next hour of tasks is split across the free nurses right away.</span>}
        </div>
      )}
    </div>
  );
}

/** A V8 card whose feed is not connected yet (Care Staff: credentials, survey items). */
export function NotConnectedCard({ label, head, body, tint }: { label: string; head: string; body: string; tint?: boolean }) {
  return (
    <div data-screen-label={label} style={sx(`flex:0 0 auto;display:flex;flex-direction:column;gap:4px;padding:10px 12px;margin-bottom:8px;border-radius:10px;${tint ? 'background:rgba(47,92,151,.07);' : 'border:1px solid rgba(34,39,46,.12);background:#FCFAF4;'}min-width:0;`)}>
      <span style={sx('font-size:16px;font-weight:800;')}>{head}</span>
      <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{body}</span>
    </div>
  );
}

/** New medicine watch, the nurse's and the Facility Admin's view: the steps due, Done and Chart. */
export function WatchStepsBlock({ label, head, sub, rows, canAct, onDone, onChart, off }: {
  label: string;
  head: string;
  sub: string;
  rows: WatchStepRow[];
  canAct: boolean;
  onDone: (r: WatchStepRow) => void;
  onChart: (residentId: string) => void;
  off: Off;
}) {
  return (
    <div data-screen-label={label} data-testid="med-watch" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:4px;padding:10px 12px;margin-bottom:8px;border-radius:10px;background:rgba(194,112,47,.10);min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px 12px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:800;')}>{head}</span>
        <span style={sx('font-size:14px;color:#54606C;')}>{sub}</span>
      </div>
      {rows.map((s) => (
        <div key={s.key} style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
          <div style={sx('flex:1 1 280px;min-width:0;')}>
            <div style={sx('font-size:16px;font-weight:700;')}>{s.k}</div>
            <div style={sx('font-size:14px;color:#3C454F;line-height:1.4;')}>{s.v}</div>
          </div>
          {canAct && (
            <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;" ink onClick={() => onDone(s)} disabled={off.disabled} title={off.title}>
              Done
            </A>
          )}
          <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;" onClick={() => onChart(s.residentId)}>
            Chart
          </A>
        </div>
      ))}
    </div>
  );
}

/** New medicine watch, care staff: what to watch for, each sign a tap to report (med-watch.js signs). */
export function WatchSignsBlock({ head, rows, reported, onReport, onChart, off }: {
  head: string;
  rows: WatchSignRow[];
  reported: Record<string, string>;
  onReport: (r: WatchSignRow, sign: string) => void;
  onChart: (residentId: string) => void;
  off: Off;
}) {
  return (
    <div data-screen-label="Worklist · New medicine watch" data-testid="med-watch" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:4px;padding:10px 12px;margin-bottom:8px;border-radius:10px;background:rgba(194,112,47,.10);min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px 12px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:800;')}>{head}</span>
        <span style={sx('font-size:14px;color:#54606C;')}>Tell the nurse straight away if you see any of these.</span>
      </div>
      {rows.map((s) => (
        <div key={s.key} style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
          <div style={sx('flex:1 1 280px;min-width:0;')}>
            <div style={sx('font-size:16px;font-weight:700;')}>{s.k}</div>
            <div style={sx('font-size:14px;color:#3C454F;line-height:1.4;')}>{s.v}</div>
          </div>
          <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;" onClick={() => onChart(s.residentId)}>
            Chart
          </A>
          {s.signs.length > 0 && (
            <div style={sx('flex:1 1 100%;')}>
              <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                {s.signs.map((g) =>
                  reported[`${s.watchId}:${g.key}`] ? (
                    <span key={g.key} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;background:#FBEDEC;color:#8A1C16;font-size:16px;font-weight:700;')}>
                      {`${g.text} · reported ${reported[`${s.watchId}:${g.key}`]}`}
                    </span>
                  ) : (
                    <A key={g.key} style="display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;" onClick={() => onReport(s, g.key)} disabled={off.disabled} title={off.title}>
                      {g.text}
                    </A>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Med pass, Facility Admin: the one "Also today" row; each chip jumps to its details under the doses. */
export function AlsoTodayRow({ chips }: { chips: { k: string; n: number | null; target: string }[] }) {
  const jump = (label: string) => {
    const w = document.querySelector<HTMLElement>(`[data-screen-label="${label}"]`);
    w?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };
  return (
    <div data-screen-label="Worklist med pass · Safety row" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;min-width:0;')}>
      <span style={sx('font-size:16px;font-weight:700;')}>Also today:</span>
      {chips.map((x) => (
        <A
          key={x.k}
          style="flex:0 0 auto;white-space:nowrap;display:inline-flex;align-items:center;gap:6px;min-height:36px;padding:0 12px;border-radius:18px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;"
          onClick={() => jump(x.target)}
        >
          <span>{x.k}</span>
          {x.n !== null && <span style={sx('font-variant-numeric:tabular-nums;color:#8A5A00;')}>{String(x.n)}</span>}
        </A>
      ))}
      <span style={sx('font-size:14px;color:#54606C;')}>Details are below the doses.</span>
    </div>
  );
}

/** Med pass, Facility Admin: the four medicine safety cards. None has a route yet: each says so. */
export function SafetyCards() {
  const card = (kicker: string, head: string | null, body: string) => (
    <div key={kicker} style={sx('display:flex;flex-direction:column;padding:10px 12px;border-radius:10px;background:#FCFAF4;border:1px solid rgba(34,39,46,.12);min-width:0;')}>
      <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;')}>{kicker}</span>
      {head && <span style={sx('font-size:16px;font-weight:800;line-height:1.35;margin-top:2px;')}>{head}</span>}
      <span style={sx('font-size:14px;color:#54606C;margin:2px 0 4px;line-height:1.4;')}>{body}</span>
    </div>
  );
  return (
    <div data-screen-label="Worklist med pass · Medicine safety" style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(380px,1fr));gap:10px;margin-bottom:8px;min-width:0;')}>
      {card('Back from hospital', 'Not connected yet', 'Discharge changes show here to confirm against the order once discharge summaries are read from the record system.')}
      {card('Check before you give', null, 'The dose asks for the reading when its order needs one. A list of today’s checks is not connected yet.')}
      {card('As-needed doses: did it work?', null, 'The dose record asks how the resident responded where the order needs it. A follow-up list is not connected yet.')}
      {card('Pharmacist review', 'Not connected yet', 'The monthly pharmacist recommendations show here once the pharmacy review is connected.')}
    </div>
  );
}
